'use client';

import { Box } from '@terra/ui-styled-system';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import type { ReactNode } from 'react';
import type { ChatObjectNavigationTarget } from '../components/hitl-types';
import { ParallelAgentsList } from './ParallelAgentsList';
import { ParallelAgentsPill } from './ParallelAgentsPill';
import type { ParallelAgentExecution } from './parallel-agent-execution';
import type { SubagentOpenTarget } from './subagent-activity';
import { SubagentActivityPanel } from './SubagentActivityPanel';
import {
  COLLAPSED_RUNNING_AGENTS_VIEW as COLLAPSED_VIEW,
  type RunningAgentsState,
} from './useRunningAgents';

export type RunningAgentsProps = RunningAgentsState & {
  messages?: TerraUIMessage[];
  onStop?: (execution: ParallelAgentExecution) => void;
  canStopExecution?: (execution: ParallelAgentExecution) => boolean;
  onOpenActivity?: (target: SubagentOpenTarget) => void;
  onOpenObject?: (target: ChatObjectNavigationTarget) => void;
  renderNeoSteps?: (threadId: string) => ReactNode;
};

export function RunningAgents({
  messages = [],
  onOpenActivity,
  onOpenObject,
  renderNeoSteps,
  batchId,
  members,
  summary,
  isVisible,
  isLeaving,
  view,
  setView,
  onStop,
  canStopExecution,
}: RunningAgentsProps) {
  if (!batchId || !isVisible) {
    return null;
  }

  const activeView =
    view.panel !== 'collapsed' && view.batchId === batchId
      ? view
      : COLLAPSED_VIEW;
  const isExpanded = activeView.panel !== 'collapsed';
  const selected =
    activeView.panel === 'detail'
      ? members.find(
          (execution) => execution.toolCallId === activeView.toolCallId,
        )
      : undefined;

  return (
    <>
      {isExpanded ? (
        <Box
          position="absolute"
          bottom="100%"
          left={0}
          right={0}
          mb={1}
          zIndex={2}
        >
          <ParallelAgentsList
            members={members}
            messages={messages}
            selectedToolCallId={selected?.toolCallId}
            onOpen={(toolCallId) =>
              setView(
                selected?.toolCallId === toolCallId
                  ? { panel: 'list', batchId }
                  : { panel: 'detail', batchId, toolCallId },
              )
            }
            onStop={onStop}
            canStopExecution={canStopExecution}
          />
        </Box>
      ) : null}
      <ParallelAgentsPill
        members={members}
        summary={summary}
        isLeaving={isLeaving}
        isExpanded={isExpanded}
        onToggle={() =>
          setView(isExpanded ? COLLAPSED_VIEW : { panel: 'list', batchId })
        }
      />
      {selected ? (
        <SubagentActivityPanel
          execution={selected}
          messages={messages}
          onOpenActivity={onOpenActivity}
          onOpenObject={onOpenObject}
          renderNeoSteps={renderNeoSteps}
          onBack={() => setView({ panel: 'list', batchId })}
        />
      ) : null}
    </>
  );
}
